import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { extractArray } from '@/features/http'
import { queryKeys } from '@/lib/query-keys'
import type { Capability,MeResponse,Role } from '@/types'

/// Staff roles (system + custom) and their capability sets, resolved server-side.
export const rolesApi = {
  list: async (): Promise<Role[]> => extractArray<Role>(await api.get<unknown>('/pos/roles')),
  create: (data: { name: string; capabilities: Capability[] }) =>
    api.post<Role[]>('/pos/roles', data),
  update: (id: string, data: { name?: string; capabilities?: Capability[] }) =>
    api.patch<Role[]>(`/pos/roles/${id}`, data),
  remove: (id: string) => api.delete(`/pos/roles/${id}`).then(() => undefined),
}

/// The current principal + derived role/capabilities. Backed by GET /auth/me.
export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.get<MeResponse>('/auth/me'),
    staleTime: 5 * 60_000,
  })
}

/// True when the signed-in user's role grants `capability`. UI gating only —
/// the backend independently enforces every capability.
export function useCan(capability: Capability): boolean {
  const { data } = useMe()
  return data?.capabilities?.includes(capability) ?? false
}
